import React, { useState } from 'react';
import { StickyNote, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getNotesForClient,
  addSessionNote,
  deleteSessionNote,
  generateId,
} from '../lib/profileStorage';
import { SERVICES } from '../lib/services';

export default function SessionNotesList({ clientId, canEdit = true, testIdPrefix = 'note' }) {
  const [notes, setNotes] = useState(() => getNotesForClient(clientId));
  const [draft, setDraft] = useState({
    date: new Date().toISOString().slice(0, 10),
    serviceId: SERVICES[0].id,
    note: '',
    staff: '',
  });
  const [showForm, setShowForm] = useState(false);

  const refresh = () => setNotes(getNotesForClient(clientId));

  const handleAdd = () => {
    if (!draft.note.trim()) {
      toast.error('Please enter a note.');
      return;
    }
    const svc = SERVICES.find((s) => s.id === draft.serviceId);
    const record = {
      id: generateId('sn'),
      clientId,
      date: draft.date,
      serviceId: draft.serviceId,
      serviceName: svc ? `${svc.name}${svc.subtitle ? ` (${svc.subtitle})` : ''}` : draft.serviceId,
      note: draft.note.trim(),
      staff: draft.staff.trim(),
      createdAt: new Date().toISOString(),
    };
    addSessionNote(record);
    refresh();
    setDraft({
      date: new Date().toISOString().slice(0, 10),
      serviceId: SERVICES[0].id,
      note: '',
      staff: '',
    });
    setShowForm(false);
    toast.success('Session note added.');
  };

  const handleDelete = (id) => {
    deleteSessionNote(id);
    refresh();
    toast.success('Session note removed.');
  };

  return (
    <div data-testid={`${testIdPrefix}-list`}>
      {canEdit && (
        <div className="mb-3">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="pill-btn inline-flex items-center gap-2 h-10 px-4 rounded-full bg-blue-500 hover:bg-blue-500 text-white text-sm font-semibold shadow-md shadow-blue-500/25"
              data-testid={`${testIdPrefix}-open-form`}
            >
              <Plus className="w-4 h-4" />
              Add session note
            </button>
          ) : (
            <div
              className="p-4 rounded-2xl bg-blue-500/10/60 border border-blue-500/20 space-y-3"
              data-testid={`${testIdPrefix}-form`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-300 font-bold mb-1 block">Date</label>
                  <input
                    type="date"
                    value={draft.date}
                    onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl border border-white/10 bg-white text-sm text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    data-testid={`${testIdPrefix}-date`}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-300 font-bold mb-1 block">Service</label>
                  <select
                    value={draft.serviceId}
                    onChange={(e) => setDraft({ ...draft, serviceId: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl border border-white/10 bg-white text-sm text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    data-testid={`${testIdPrefix}-service`}
                  >
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.subtitle ? ` — ${s.subtitle}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-300 font-bold mb-1 block">Staff</label>
                  <input
                    type="text"
                    value={draft.staff}
                    onChange={(e) => setDraft({ ...draft, staff: e.target.value })}
                    placeholder="Your name (optional)"
                    className="w-full h-11 px-3 rounded-xl border border-white/10 bg-white text-sm text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                    data-testid={`${testIdPrefix}-staff`}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-300 font-bold mb-1 block">Notes</label>
                <textarea
                  value={draft.note}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                  placeholder="Treatment area, response, aftercare, follow-up..."
                  rows={3}
                  className="w-full px-3 py-3 rounded-xl border border-white/10 bg-white text-sm text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                  data-testid={`${testIdPrefix}-body`}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  className="h-10 px-4 rounded-full bg-blue-500 hover:bg-blue-500 text-white font-semibold text-sm shadow-md shadow-blue-500/25"
                  data-testid={`${testIdPrefix}-save`}
                >
                  Save note
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {notes.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-3">
          No session notes yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li
              key={n.id}
              className="p-4 rounded-2xl border border-white/10 bg-white flex items-start gap-3"
              data-testid={`${testIdPrefix}-item-${n.id}`}
            >
              <div className="w-9 h-9 rounded-2xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center shrink-0">
                <StickyNote className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <p className="text-sm font-semibold text-white">
                    {n.serviceName}
                  </p>
                  <p className="text-xs text-slate-400">
                    {n.date}
                    {n.staff && <span> · by {n.staff}</span>}
                  </p>
                </div>
                <p className="text-sm text-slate-200 mt-1 whitespace-pre-line">
                  {n.note}
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={() => handleDelete(n.id)}
                  className="text-slate-400 hover:text-red-500 shrink-0"
                  data-testid={`${testIdPrefix}-delete-${n.id}`}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
